import type { IObservableArray } from 'mobx';
import { runInAction } from 'mobx';
import { useCallback } from 'react';

import type { Campaign, Targetable, Targeting } from '@feathr/blackbox';
import { TargetableClass } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';

import {
  GEO_TARGETING_KINDS,
  getGeoFilterIds,
  removeOrArchiveTargetings,
} from '../Targetings.utils';
import { type IGeoFilterSetData } from './GeoFilterModal';

interface IUseGeoFilterSetOperationsProps {
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
}

interface IUseGeoFilterSetOperationsReturn {
  saveCustomSet: (name: string) => Promise<Targetable>;
  applyGeoFilterSet: (geoFilterSetId: string) => Promise<void>;
  updateGeoFilterSet: (targetableId: string, data: IGeoFilterSetData) => Promise<void>;
}

export function useGeoFilterSetOperations({
  campaign,
  targetings,
}: IUseGeoFilterSetOperationsProps): IUseGeoFilterSetOperationsReturn {
  const { Targetables, Targetings } = useStore();

  /**
   * Save current geo targetings as a new custom geo filter set.
   * Archives existing geo targetings and creates a new geo_set targeting.
   * All persisted archived geo targetings are saved to the server before adding
   * the new targeting to prevent server sync from overwriting local archived state.
   */
  const saveCustomSet = useCallback(
    async (name: string): Promise<Targetable> => {
      const { included, excluded } = await getGeoFilterIds(targetings, Targetables);

      if (included.length === 0 && excluded.length === 0) {
        throw new Error('No geofilters to save');
      }

      const geoSet = Targetables.create({
        _cls: TargetableClass.geo_set,
        name,
        included_geo_filters: included,
        excluded_geo_filters: excluded,
      });

      await Targetables.add(geoSet, { refreshApiCache: false });

      const activeGeoTargetings = targetings.filter(
        (t) => GEO_TARGETING_KINDS.includes(t.get('kind')) && !t.get('is_archived'),
      );

      removeOrArchiveTargetings({
        targetingsToRemove: activeGeoTargetings,
        targetingsArray: targetings,
        Targetings,
      });

      // Save ALL persisted archived geo targetings to server BEFORE adding new targeting.
      // This includes:
      // - Targetings we just archived above
      // - Targetings that were archived earlier (e.g., when switching from saved set to custom mode)
      // This prevents server sync from overwriting is_archived back to false.
      const allArchivedPersistedGeoTargetings = targetings.filter(
        (t) =>
          GEO_TARGETING_KINDS.includes(t.get('kind')) &&
          t.get('is_archived') === true &&
          !t.isEphemeral,
      );

      if (allArchivedPersistedGeoTargetings.length > 0) {
        await Promise.all(allArchivedPersistedGeoTargetings.map((t) => t.save()));
      }

      const newTargeting = Targetings.create({
        parent: campaign.get('id'),
        kind: 'geo_set',
        included: true,
        target_data: geoSet.id,
      });

      runInAction(() => {
        targetings.push(newTargeting);
      });

      await Targetings.add(newTargeting, { refreshApiCache: false });

      return geoSet;
    },
    [campaign, targetings, Targetables, Targetings],
  );

  /**
   * Apply a geo filter set to the campaign by replacing current geo targetings
   */
  const applyGeoFilterSet = useCallback(
    async (geoFilterSetId: string): Promise<void> => {
      const allGeoTargetings = targetings.filter(
        (t) => GEO_TARGETING_KINDS.includes(t.get('kind')) && !t.get('is_archived'),
      );

      const newTargeting = Targetings.create({
        parent: campaign.get('id'),
        kind: 'geo_set',
        included: true,
      });

      removeOrArchiveTargetings({
        targetingsToRemove: allGeoTargetings,
        targetingsArray: targetings,
        Targetings,
      });

      runInAction(() => {
        targetings.push(newTargeting);

        // Make it dirty by setting after creation
        newTargeting.set({
          target_data: geoFilterSetId,
        });
      });
    },
    [campaign, targetings, Targetings],
  );

  /**
   * Update an existing geo filter set.
   * Note: is_default is not saved on the targetable - it's managed via account settings.
   */
  const updateGeoFilterSet = useCallback(
    async (targetableId: string, data: IGeoFilterSetData): Promise<void> => {
      const targetable = Targetables.get(targetableId);
      if (!targetable) {
        throw new Error('Targetable not found');
      }

      runInAction(() => {
        targetable.set({
          name: data.name,
          included_geo_filters: data.included_geo_filters,
          excluded_geo_filters: data.excluded_geo_filters,
        });
      });

      await targetable.save();
    },
    [Targetables],
  );

  return {
    saveCustomSet,
    applyGeoFilterSet,
    updateGeoFilterSet,
  };
}

export default useGeoFilterSetOperations;
